import React from "react";
import { useField } from "formik";
import { Helmet } from "react-helmet";
import Dropdown from "./Dropdown";
import Link from "../Link";

export const ErrorModalHelper = ({
  children,
  closeFunction,
  message,
  ...props
}) => {
  return (
    <div
      className={`fixed inset-0 z-modal flex flex-col justify-center items-center bg-black/75 p-6 transition-all delay-500 duration-300 ${
        message ? "visible opacity-100" : "invisible opacity-0"
      }`}
      onClick={() => closeFunction(false)}
    >
      <div
        className={`w-full max-w-[600px] rounded p-6 bg-error text-white relative pr-8 transition-all delay-0 duration-1000 ${
          message ? "translate-y-0" : "translate-y-80"
        }`}
      >
        {message}
        <Link
          icon="close"
          to=""
          className="absolute right-2 top-2"
          onClick={() => closeFunction(false)}
        />
      </div>
      {message ? (
        <Helmet>
          <body className="brand-modal-open" />
        </Helmet>
      ) : (
        <p />
      )}
    </div>
  );
};

export const Label = ({ children, label, as, ...props }) => {
  if (as === "radio" || as === "checkbox") {
    return (
      <label
        htmlFor={props.htmlFor}
        className={`flex flex-row gap-2 items-center justify-start`}
      >
        {children}
        <span class="text-sm font-bold">{label}</span>
      </label>
    );
  }
  return (
    <label htmlFor={props.htmlFor} className="block text-sm font-bold mb-2">
      {label || children}
    </label>
  );
};

export const ErrorInlineHelper = ({ children, ...props }) => {
  return (
    <div className="flex flex-row items-center text-error text-xs my-3">
      <span className="material-icons text-[16px] mr-1">warning</span>
      {children}
    </div>
  );
};

export const TextInput = ({ children, label, ...props }) => {
  // useField() returns [formik.getFieldProps(), formik.getFieldMeta()]
  // which we can spread on <input>. We can use field meta to show an error
  // message if the field is invalid and it has been touched (i.e. visited)
  const [field, meta] = useField(props);
  return (
    <div className="w-full mb-6">
      <Label htmlFor={props.id || props.name}>{label || children}</Label>
      <input
        className={`transition-all brand-form-item brand-form-input brand-form-padding brand-form-style focus:brand-form-focus${
          meta.touched && meta.error ? " brand-form-control-error" : ""
        }`}
        {...field}
        {...props}
      />
      {meta.touched && meta.error ? (
        <ErrorInlineHelper>{meta.error}</ErrorInlineHelper>
      ) : null}
    </div>
  );
};

export const TextArea = ({ children, label, ...props }) => {
  // useField() returns [formik.getFieldProps(), formik.getFieldMeta()]
  // which we can spread on <input>. We can use field meta to show an error
  // message if the field is invalid and it has been touched (i.e. visited)
  const [field, meta] = useField(props);
  return (
    <div className="w-full mb-6">
      <Label htmlFor={props.id || props.name}>{label || children}</Label>
      <textarea
        className={`transition-all brand-form-item brand-form-textarea brand-form-padding brand-form-style focus:brand-form-focus${
          meta.touched && meta.error ? " brand-form-control-error" : ""
        }`}
        {...field}
        {...props}
      />
      {meta.touched && meta.error ? (
        <ErrorInlineHelper>{meta.error}</ErrorInlineHelper>
      ) : null}
    </div>
  );
};

export const Checkbox = ({ children, ...props }) => {
  // React treats radios and checkbox inputs differently from other input types: select and textarea.
  // Formik does this too! When you specify `type` to useField(), it will
  // return the correct bag of props for you -- a `checked` prop will be included
  // in `field` alongside `name`, `value`, `onChange`, and `onBlur`
  const [field, meta] = useField({ ...props, type: "checkbox" });
  return (
    <div className="w-full mb-6">
      <Label
        htmlFor={props.id || props.name}
        label={props.label || children}
        as="checkbox"
      >
        <input
          type="checkbox"
          {...field}
          {...props}
          className={`transition-all brand-form-checkbox border-2 border-black rounded focus:brand-form-focus${
            meta.touched && meta.error ? " brand-form-control-error" : ""
          }`}
        />
      </Label>
      {meta.error ? <ErrorInlineHelper>{meta.error}</ErrorInlineHelper> : null}
    </div>
  );
};

export const Radio = ({ children, ...props }) => {
  // React treats radios and checkbox inputs differently from other input types: select and textarea.
  // Formik does this too! When you specify `type` to useField(), it will
  // return the correct bag of props for you -- a `checked` prop will be included
  // in `field` alongside `name`, `value`, `onChange`, and `onBlur`
  const [field, meta] = useField({ ...props, type: "radio" });
  return (
    <Label htmlFor={props.id} label={props.label || children} as="radio">
      <input
        type="radio"
        {...field}
        {...props}
        className="brand-form-radio brand-form-style focus:brand-form-focus"
      />
    </Label>
  );
};

export const RadioGroup = ({ children, ...props }) => {
  const [field, meta] = useField({ ...props, type: "radio" });
  return (
    <div className={`w-full mb-6`}>
      <div
        className={`transition-all flex flex-col gap-1${
          meta.touched && meta.error
            ? " brand-form-control-error rounded p-3"
            : ""
        }`}
        role="group"
      >
        {children}
      </div>
      {meta.touched && meta.error ? (
        <ErrorInlineHelper>{meta.error}</ErrorInlineHelper>
      ) : null}
    </div>
  );
};

export const Select = ({ label, options, ...props }) => {
  const [field, meta, helpers] = useField(props);
  const { setValue, setTouched, setError } = helpers;

  return (
    <div className="w-full mb-6">
      <Label
        htmlFor={props.id || props.name}
        label={label}
        id={`${props.id}_label`}
      />
      <Dropdown
        options={options}
        placeholder="What do you do?"
        value={field.value}
        onChange={setValue}
        onBlur={setTouched}
        style="form"
        namespace={props.id}
        className={
          meta.touched && meta.error ? " brand-form-control-error" : ""
        }
      />
      {meta.touched && meta.error ? (
        <ErrorInlineHelper>{meta.error}</ErrorInlineHelper>
      ) : null}
    </div>
  );
};
/*
export const SelectOG = ({ label, options, ...props }) => {
  const [field, meta, helpers] = useField(props)
  const { setValue, setTouched, setError } = helpers

  return (
    <div className="w-full mb-6">
      <Label htmlFor={props.id || props.name} label={label} />
      <input type="hidden" {...field} {...props} />
      <Dropdown
        name={props.name}
        options={options}
        placeholder={props.placeholder}
        onSelectFunction={setValue}
        onTouchedFunction={setTouched}
        onErrorFunction={setError}
        enableTab={true}
        className={
          meta.touched && meta.error ? " brand-form-control-error" : ""
        }
      />
      {meta.touched && meta.error ? (
        <ErrorInlineHelper>{meta.error}</ErrorInlineHelper>
      ) : null}
    </div>
  )
}
*/

/*

      <Dropdown
        name={props.name}
        options={options}
        placeholder={props.placeholder}
        onSelectFunction={setValue}
        onTouchedFunction={setTouched}
        onErrorFunction={setError}
        enableTab={true}
        className={
          meta.touched && meta.error ? " brand-form-control-error" : ""
        }
      />

*/
